// Imports
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import React, {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import styled from 'styled-components/macro';
import { GetFeeParameters } from '../../../../../../shareables/types/api/main/website/payment-option/fee/get';
import { GetPaymentIntentResponse } from '../../../../../../shareables/types/api/main/website/payment-option/payment-intent/get';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Checkbox from '../../../../../../shareables/foundation/front-end/components/forms/fields/checkbox';
import APIConnectedFormContext, { ContextDetails } from '../../../../../../shareables/contexts/api-connected-form';
import ExpectedFormError from '../../../../../../shareables/utils/errors/expected-form-error';
import handleStripeConfirmationError from '../../../../../../shareables/front-end/stripe-component-wrappers/utils/handle-stripe-confirmation-error';
import scrollIntoViewIfNeeded from '../../../../../../shareables/foundation/front-end/utils/scroll-into-view-if-needed';


// Styled components
const SaveCardWrapper = styled.div`
	padding-top: 1.25em;
`;


// Define the accepted ref
export interface StripePaymentElementRef {
	handleSubmit: (clientSecret: GetPaymentIntentResponse) => Promise<void>;
}


// Define the accepted props
interface StripePaymentElementProps {
	onSuccess: () => void;
	paymentOptionID: string;
	onPaymentMethodCountryCodeChange: (paymentMethodCountryCode: GetFeeParameters['methodCountryCode']) => void;
	onSaveCardChange: (saveCard: boolean) => void;
	isActive: boolean;
}


// Function component
const StripePaymentElement = forwardRef<StripePaymentElementRef, StripePaymentElementProps>(
	({ onSuccess, paymentOptionID, onPaymentMethodCountryCodeChange, onSaveCardChange, isActive }, ref) => {
		// Use state
		const [nameOnCard, setNameOnCard] = useState('');
		const [nameError, setNameError] = useState('');
		
		
		// Use ref
		const paymentElementWrapperRef = useRef<HTMLDivElement>(null);
		
		
		// Use Stripe hooks
		const stripe = useStripe();
		const elements = useElements();
		
		
		// Use context
		const context = useContext(APIConnectedFormContext);
		
		
		// Function that attempts to return the payment method's country code
		const getMethodCountryCodeToken = useCallback(async (): Promise<string> => {
			// Validate name on card
			if (!nameOnCard.trim()) {
				setNameError('This is required.');
				throw new ExpectedFormError();
			}
			
			
			// Handle if Stripe.js didn't load for some reason
			if (!stripe || !elements) {
				throw new Error('Stripe.js hasn’t loaded');
			}
			
			
			// Trigger form validation
			const { error: submitError } = await elements.submit();
			
			if (submitError) {
				scrollIntoViewIfNeeded(paymentElementWrapperRef.current);
				throw new ExpectedFormError();
			}
			
			
			// Derive the card's country
			const { confirmationToken } = await stripe.createConfirmationToken({ elements });
			
			
			// Return
			return (confirmationToken?.payment_method_preview?.card?.country ?? undefined) as string;
		}, [nameOnCard, stripe, elements]);
		
		
		// Add and remove the token getter from the connected form
		let addTokenGetter: NonNullable<ContextDetails>['addTokenGetter'] | null = null;
		let removeTokenGetter: NonNullable<ContextDetails>['removeTokenGetter'] | null = null;
		
		if (context) {
			addTokenGetter = context.addTokenGetter;
			removeTokenGetter = context.removeTokenGetter;
		}
		
		useEffect(() => {
			if (addTokenGetter === null || !isActive) {
				return;
			}
			
			addTokenGetter('methodCountryCode', getMethodCountryCodeToken);
			
			return () => {
				if (removeTokenGetter === null) {
					return;
				}
				
				removeTokenGetter('methodCountryCode');
			};
		}, [addTokenGetter, removeTokenGetter, getMethodCountryCodeToken, isActive]);
		
		
		// Handle submit
		const handleSubmit = async (clientSecret: GetPaymentIntentResponse) => {
			// Handle if Stripe.js didn't load for some reason
			if (!stripe || !elements) {
				throw new Error('Stripe.js hasn’t loaded');
			}
			
			
			// Validate name on card
			if (!nameOnCard.trim()) {
				setNameError('This is required.');
				return;
			}
			
			
			// Trigger form validation
			const { error: submitError } = await elements.submit();
			
			
			// Handle error
			if (submitError) {
				scrollIntoViewIfNeeded(paymentElementWrapperRef.current);
				return;
			}
			
			
			// Confirm the payment intent using the details collected by the payment element
			const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
				elements: elements,
				clientSecret: clientSecret,
				redirect: 'if_required',
				confirmParams: {
					return_url: `https://${window.location.hostname}/payments/pay?id=${paymentOptionID}`,
					payment_method_data: { billing_details: { name: nameOnCard } },
				},
			});
			
			
			// Handle error
			if (confirmError) {
				handleStripeConfirmationError(confirmError, { confirmedWithPaymentElement: true });
				scrollIntoViewIfNeeded(paymentElementWrapperRef.current);
				return;
			}
			
			
			// Handle non-redirected success
			if (paymentIntent) {
				onSuccess();
			}
		};
		
		
		// Handle payment method change
		const handlePaymentMethodChange = async (event: StripePaymentElementChangeEvent) => {
			// Skip if payment element isn't active
			if (!isActive) {
				return;
			}
			
			
			// Handle incomplete card
			//  -> Clear country so fee doesn't carry over from a previous card
			if (!event.complete) {
				onPaymentMethodCountryCodeChange(undefined);
				return;
			}
			
			
			// Handle if Stripe.js didn't load for some reason
			if (!stripe || !elements) {
				throw new Error('Stripe.js hasn’t loaded');
			}
			
			
			// Trigger form validation
			const { error: submitError } = await elements.submit();
			
			
			// Create token and get/set the payment method's country code
			if (!submitError) {
				const { confirmationToken } = await stripe.createConfirmationToken({ elements });
				onPaymentMethodCountryCodeChange(confirmationToken?.payment_method_preview?.card?.country ?? undefined);
			}
		};
		
		
		// Handle payment method blur
		const handlePaymentMethodBlur = async () => {
			// Skip if payment element isn't active
			if (!isActive) {
				return;
			}
			
			
			// Handle if Stripe.js didn't load for some reason
			if (!stripe || !elements) {
				return;
			}
			
			
			// Trigger form validation
			const { error: submitError } = await elements.submit();
			
			
			// Create token and get/set the payment method's country code
			if (!submitError) {
				const { confirmationToken } = await stripe.createConfirmationToken({ elements });
				onPaymentMethodCountryCodeChange(confirmationToken?.payment_method_preview?.card?.country ?? undefined);
			}
		};
		
		
		// Expose the method to parent
		useImperativeHandle(ref, () => ({
			handleSubmit,
		}));
		
		
		// Create options
		const options: StripePaymentElementOptions = useMemo(
			() => ({
				layout: 'tabs',
				terms: {
					card: 'never',
				},
			}),
			[]
		);
		
		
		// Return JSX
		return (
			<React.Fragment>
				<Input
					name='nameOnCard'
					label='Name on card'
					type='text'
					onChange={(newValue) => {
						setNameOnCard(newValue);
						
						if (nameError) {
							setNameError('');
						}
					}}
					placeholder='John Doe'
					errorMessage={nameError}
					required={isActive}
					exclude
				/>
				
				<div ref={paymentElementWrapperRef}>
					<PaymentElement onChange={handlePaymentMethodChange} onBlur={handlePaymentMethodBlur} options={options} />
				</div>
				
				<SaveCardWrapper>
					<Checkbox
						name='saveCard'
						label='Save this new card for faster payments later'
						exclude={!isActive}
						onChange={onSaveCardChange}
					/>
				</SaveCardWrapper>
			</React.Fragment>
		);
	}
);


// @ts-expect-error: displayName does exist
StripePaymentElement.displayName = 'StripePaymentElement';

export default StripePaymentElement;
